import React, { useState } from 'react';
import ExitIcon from '@moda/icons/exit-16';
import { Clickable, ControlLink, Stack, Text } from '@moda/om';
import { noop } from '@moda/portal-stanchions';
import { useUser } from '../../hooks/useUser';
import { RegistrationChallenge } from '../../lib/passwordless';
import { ModalOverlay } from '../Modal';
import { PasswordlessOtpStep } from '../Authentication/PasswordlessOtpStep';
import { PasswordlessRegistrationStep } from '../Authentication/PasswordlessRegistrationStep';
import { PasswordlessRequestStep } from '../Authentication/PasswordlessRequestStep';
import { hrefFor } from '../../routers';
import { ConvertGuestModalProps } from './ConvertGuestModal';

type PasswordlessStep =
  | { step: 'request' }
  | { step: 'otp'; email: string; challengeId: string }
  | ({ step: 'registration' } & RegistrationChallenge);

export const ConvertGuestPasswordlessModal: React.FC<ConvertGuestModalProps> = ({
  onAuth = noop,
  onCloseModal,
  show,
  title
}) => {
  const {
    user: { accessToken, email: userEmail }
  } = useUser();
  const [step, setStep] = useState<PasswordlessStep>({ step: 'request' });

  return (
    <ModalOverlay onClose={onCloseModal || noop} show={show}>
      <div className="ConvertGuestModal">
        <Clickable aria-label="Close" className="ConvertGuestModal__close" onClick={onCloseModal}>
          <ExitIcon />
        </Clickable>

        <Stack space={4}>
          {step.step === 'request' && (
            <>
              <Stack space={4}>
                <Text treatment="h4" family="serif">
                  {title}
                </Text>
                <Text treatment="h6">
                  Create an account to save your shipment and payment preferences, check your order
                  status, and more.
                </Text>
              </Stack>
              <div className="ConvertGuestModal__passwordless">
                <PasswordlessRequestStep
                  defaultEmail={userEmail || undefined}
                  disableEmail
                  hideAuthModal={onCloseModal}
                  message=""
                  onAuth={onAuth}
                  onRequested={({ challengeId, email }) =>
                    setStep({ step: 'otp', challengeId, email })
                  }
                  showCancelButton={false}
                  showSocialAuth={false}
                  title=""
                />
              </div>
              <Text treatment="body2">
                To opt out,{' '}
                <ControlLink to={hrefFor.UnsubscribePage()}>unsubscribe here</ControlLink> or in our
                emails.
              </Text>
            </>
          )}

          {step.step === 'otp' && (
            <div className="ConvertGuestModal__passwordless">
              <PasswordlessOtpStep
                challengeId={step.challengeId}
                email={step.email}
                onAuth={onAuth}
                onCancel={() => setStep({ step: 'request' })}
                onRegistrationRequired={registration =>
                  setStep({ step: 'registration', ...registration })
                }
                title=""
              />
            </div>
          )}

          {step.step === 'registration' && (
            <div className="ConvertGuestModal__passwordless">
              <PasswordlessRegistrationStep
                authorizationToken={accessToken}
                email={step.email}
                message="Create an account to save your shipment and payment preferences, check your order status, and more."
                onAuth={onAuth}
                onCancel={() => setStep({ step: 'request' })}
                registrationToken={step.registrationToken}
                remember
                title=""
              />
            </div>
          )}
        </Stack>
      </div>
    </ModalOverlay>
  );
};
